import type { AlignmentSignal } from './api';

/**
 * Pure copy mapping — one honest, human sentence per signal KIND (never the
 * evidence text itself, which the card renders separately as its own chip so
 * the user always sees the ACTUAL matched words). Extracted from the card
 * component per the house pattern (pure logic separated from JSX, e.g.
 * refineThreadsSource.ts / refineDnaReveal.ts) so the label mapping is
 * unit-testable without mounting anything.
 */
export function alignmentSignalLabel(kind: AlignmentSignal['kind']): string {
  switch (kind) {
    case 'cross_employer_leakage':
      return 'mentions another employer';
    case 'domain_vocab_mismatch':
      return "reads like a different field's story";
    case 'era_mismatch':
      return "names a year outside this role's dates";
    case 'capture_context_mismatch':
      return 'your own words pointed somewhere else';
    case 'near_duplicate':
      return 'reads like the same story told another way';
    default:
      return 'flagged for review';
  }
}

// ─────────────────────────────────────────────────────────────────────────────
// NEAR-DUPLICATE CLUSTER DISPLAY (RESUME-POLISH lane, part c) — pure helpers
// for the deck's cluster card variant. INDEPENDENT implementation of the same
// richness-pick CONVENTION apps/api/lib/achievementClustering.ts's
// pickRichestRepresentative defines (most chars, tie-broken by most numeric
// facts, first-seen on a full tie) — mirrors the SAME cross-app-boundary
// precedent DriveExportService.ts's own header documents for
// buildExportFilename/lastNameFrom ("independent implementations of the same
// convention, not the same function" — a FE component cannot import
// apps/api code, so this is the honest, deliberate re-derivation, not
// duplication-by-accident). The FE never DECIDES the merge on its own
// authority, though — it only renders which member IT would call the
// survivor so the user sees the SAME choice the composer already applies
// silently, before confirming the MERGE tap that writes it.
// ─────────────────────────────────────────────────────────────────────────────

/** One cluster member as the card needs it for display + the richness pick:
 *  its own id/headline (the card's own flag) plus every OTHER member's id +
 *  headline (AlignmentSignal.clusterMembers). */
export interface ClusterMemberForDisplay {
  achievementId: string;
  headline: string;
}

/** The SAME richness score achievementClustering.ts's pickRichestRepresentative
 *  computes: character count first, numeric-fact count (digit runs) as the
 *  tiebreak. Headline-only here (the deck's clusterMembers carries only
 *  achievementId + headline, not the fuller STAR text the résumé composer's
 *  own richness pick can see) — a narrower but honest mirror of the same rule. */
function clusterMemberRichness(m: ClusterMemberForDisplay): { chars: number; numericFacts: number } {
  return { chars: m.headline.length, numericFacts: (m.headline.match(/\d+/g) ?? []).length };
}

/** Pick the richest member of a cluster (this card's own headline + every
 *  OTHER member from its near_duplicate signal's clusterMembers) — the same
 *  survivor the résumé composer would silently pick. Deterministic:
 *  first-seen (this card's own entry is always first in the list the caller
 *  builds) wins a full tie, exactly like the backend's own tiebreak. */
export function pickClusterSurvivor(
  members: ClusterMemberForDisplay[],
): ClusterMemberForDisplay {
  let best = members[0];
  let bestScore = clusterMemberRichness(best);
  for (let i = 1; i < members.length; i++) {
    const score = clusterMemberRichness(members[i]);
    if (
      score.chars > bestScore.chars ||
      (score.chars === bestScore.chars && score.numericFacts > bestScore.numericFacts)
    ) {
      best = members[i];
      bestScore = score;
    }
  }
  return best;
}
